import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Operation, OperationObserverService } from '@x/common/operation';
import {
  AbandonOrderDialogActions,
  AbandonOrderDialogConfig,
  AbandonOrderDialogResponseEnum,
} from '@x/ecommerce-shop/app/core/order/services/abandon-order.service';
import { ShopCheckoutOrderService } from '@x/ecommerce/shop-client';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { catchError, debounceTime, tap } from 'rxjs/operators';

@Component({
  selector: 'x-order-abandon-dialog',
  templateUrl: './order-abandon-dialog.component.html',
  styleUrls: ['./order-abandon-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrderAbandonDialogComponent {
  private abandonCartOrderId$ = new BehaviorSubject<number | null | undefined>(null);

  operation$: Observable<Operation<number | undefined>> = this.abandonCartOrderId$.pipe(
    debounceTime(200),
    switchMap((orderId) => {
      if (!orderId) return of(Operation.create());

      return this.operationService.observe(this.cancelOrder(orderId));
    }),
    tap((state) => {
      if (state.data) {
        this.dialogRef.close(AbandonOrderDialogResponseEnum.Yes);
      }
    }),
  );

  constructor(
    public dialogRef: MatDialogRef<OrderAbandonDialogComponent, AbandonOrderDialogResponseEnum>,
    @Inject(MAT_DIALOG_DATA)
    public data: AbandonOrderDialogConfig,
    private operationService: OperationObserverService,
    private shopCheckoutOrderService: ShopCheckoutOrderService,
  ) {}

  closeDialog() {
    this.dialogRef.close(AbandonOrderDialogResponseEnum.No);
  }

  closeAction(action: AbandonOrderDialogActions) {
    if (action.response === AbandonOrderDialogResponseEnum.No) {
      this.closeDialog();
      return;
    }

    this.abandonCartOrderId$.next(this.data.currentOrder.orderId);
  }

  cancelOrder(orderId: number): Observable<number> {
    if (this.data.currentOrder.orderState === 'QUOTED') {
      return this.shopCheckoutOrderService.cancel({ orderId }).pipe(catchError(() => of(orderId)));
    }

    return of(orderId);
  }
}
